// Who the person is on the new server, as today's Account pane shows it
// (settings/AccountSettings.tsx there): their name and workspace; their
// address, or the way to add one by a link; their passkeys, and the way to
// add one, name one and remove one; and the session's way to sign out.
// Adding either confirms with a passkey only when the server asks (store.ts,
// `addEmail`, `addPasskey`); removing a passkey always does. A
// person who signed up by email has no passkey yet: the pane says so, and
// adds one while their session is fresh. Whether their actions ask for a
// passkey is theirs to say (Poulpe-686): turning it off asks for one last
// confirmation (store.ts, `setConfirmations`), and then none of these asks.
// A token signs no session in: it has no address to add, no passkeys, no
// confirmations to turn off and no signing out.
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Me, PasskeyEntry, PasskeyList } from '../api/types'
import { Time } from '../components/Time'
import { useMounted } from '../components/layout'
import { confirmationProblemText } from '../people/confirmation'
import { SentLine } from '../people/EmailSignIn'
import { looksLikeAddress } from '../people/emailLink'
import { accountWords as w, emailWords } from '../people/words'
import { useCopiesSelection, setCopiesSelection } from '../lib/terminalCopy'
import { needsServer } from '../components/needsServer'
import { toast } from '../components/toasts'
import { SavedText } from './SavedText'
import { settingsWords } from './words'
import { useBoardState, useStore } from '../store/context'
import type { AccountOutcome } from '../store/store'

/** The most passkeys an account holds. */
const PASSKEYS_MAX = 10

/** What to tell the person when adding was not done; `failed` leads the words of any other failure. */
function addProblemText(outcome: Extract<AccountOutcome<unknown>, { ok: false }>, failed: string): string {
  return outcome.problem === 'not_fresh' ? w.notFresh : confirmationProblemText(outcome, failed)
}

export function AccountSettings() {
  const { me } = useBoardState()
  const store = useStore()
  const navigate = useNavigate()
  const mounted = useMounted()
  const [leaving, setLeaving] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  if (!me) return null
  return <section className="settings-section">
    {me.actor.kind === 'person' && me.member
      ? <ProfileSettings key={`${me.workspace.id}/${me.member.id}`} me={me} />
      : <><div className="field"><span>{w.name}</span><span className="settings-value">{me.member?.name ?? ''}</span></div>
          <div className="field"><span>{w.workspace}</span><span className="settings-value">{me.workspace.name}</span></div></>}
    <Address me={me} />
    {!store.byToken && <Passkeys />}
    {!store.byToken && <Confirmations me={me} />}
    {!store.byToken && <div className="settings-foot">
      {problem && <p role="alert">{problem}</p>}
      <button type="button" className="button" disabled={leaving} onClick={async () => {
        setLeaving(true); setProblem(null)
        const out = await store.signOut()
        if (!mounted.current) return
        setLeaving(false)
        if (out) navigate('/')
        else setProblem('Could not sign out. Try again.')
      }}>{leaving ? 'Signing out…' : 'Sign out'}</button>
    </div>}
  </section>
}

/** Names and preferences save as each field is changed, as in today's settings. */
function ProfileSettings({ me }: { me: Me }) {
  const store = useStore()
  const { offline } = useBoardState()
  const mounted = useMounted()
  const copies = useCopiesSelection()
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const save = async (change: Parameters<typeof store.setProfile>[0]) => {
    const out = await store.setProfile(change)
    if (!out.ok) throw new Error(out.message)
  }
  return <>
    <SavedText label={w.name} value={me.member?.name ?? ''} disabled={offline} failed="Could not save the name." save={name => save({ name })} />
    <SavedText label={w.workspace} value={me.workspace.name} disabled={offline} failed="Could not save the name." save={workspace => save({ workspace })} />
    <label className="field"><span>Plans</span>
      <select value={me.plan_format ?? 'html'} disabled={busy || offline} {...needsServer(offline)} onChange={async event => {
        const plan_format = event.target.value as 'html' | 'markdown'
        setBusy(true); setProblem(null)
        try { await save({ plan_format }); if (mounted.current) toast(settingsWords.saved) }
        catch (error) { if (mounted.current) setProblem(error instanceof Error ? error.message : 'Could not save the plan format.') }
        finally { if (mounted.current) setBusy(false) }
      }}><option value="html">HTML</option><option value="markdown">Markdown</option></select>
    </label>
    {problem && <p role="alert" className="text-danger">{problem}</p>}
    <label className="field"><span><input type="checkbox" checked={copies} onChange={event => setCopiesSelection(event.target.checked)} /> Copy text selected in a terminal</span></label>
  </>
}

/**
 * Whether the person confirms their actions with a passkey, as the server
 * says: turning it off asks for the passkey one last time, and turning it on
 * asks for none. Not shown to an agent, nor by an older server, which always
 * asks.
 */
function Confirmations({ me }: { me: Me }) {
  const store = useStore()
  const { offline } = useBoardState()
  const mounted = useMounted()
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  if (me.actor.kind !== 'person' || typeof me.confirms_actions !== 'boolean') return null
  const on = me.confirms_actions
  const change = async (next: boolean) => {
    setBusy(true); setProblem(null)
    const outcome = await store.setConfirmations(next)
    if (!mounted.current) return
    setBusy(false)
    if (outcome.ok) toast(settingsWords.saved)
    else setProblem(confirmationProblemText(outcome, w.confirmFailed))
  }
  return <>
    <label className="field"><span><input type="checkbox" checked={on} disabled={busy || offline} {...needsServer(offline)} onChange={event => void change(event.target.checked)} /> {w.confirmActions}</span></label>
    <p className="muted">{on ? w.confirmOn : w.confirmOff}</p>
    {problem && <p role="alert" className="text-danger">{problem}</p>}
  </>
}

/** The person's address; or, signed in by a session, the way to add one: an address, then "check your email". */
function Address({ me }: { me: Me }) {
  const store = useStore()
  const { pathname, search } = useLocation()
  const mounted = useMounted()
  const [step, setStep] = useState<'idle' | 'adding' | 'sent'>('idle')
  const [address, setAddress] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  if (me.address) return <div className="field"><span>{w.email}</span><span className="settings-value">{me.address}</span></div>
  if (store.byToken) return null
  if (step === 'sent') {
    return <div className="field">
      <span>{w.email}</span>
      <span className="settings-value account-sent" role="status">
        <strong>{emailWords.check.title}</strong>
        <span><SentLine address={address.trim()} /></span>
        <button type="button" className="link-button" onClick={() => setStep('adding')}>{emailWords.check.other}</button>
      </span>
    </div>
  }
  if (step === 'idle') {
    return <div className="field">
      <span>{w.email}</span>
      <span className="account-do"><button type="button" className="button" onClick={() => setStep('adding')}>{w.addEmail}</button></span>
    </div>
  }
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const typed = address.trim()
    if (!typed || busy) return
    if (!looksLikeAddress(typed)) { setProblem(emailWords.notAnAddress); return }
    setBusy(true); setProblem(null)
    const outcome = await store.addEmail(typed, pathname + search)
    if (!mounted.current) return
    setBusy(false)
    if (outcome.ok) setStep('sent')
    else setProblem(addProblemText(outcome, w.emailFailed))
  }
  return <form className="account-add" onSubmit={(event) => void submit(event)} noValidate>
    <label className="field">
      <span>{w.newAddress}</span>
      <input type="email" name="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={address} readOnly={busy} autoFocus
        onChange={(event) => { setAddress(event.target.value); setProblem(null) }} />
    </label>
    {problem && <p className="account-problem" role="alert">{problem}</p>}
    <div className="account-actions">
      <button type="button" className="button" disabled={busy} onClick={() => { setStep('idle'); setProblem(null) }}>{w.cancel}</button>
      <button type="submit" className="button button-primary" disabled={busy || !address.trim()}>{emailWords.go}</button>
    </div>
  </form>
}

/** The person's passkeys, each with its name, when it was added and last used, and the way to rename or remove it; and the way to add one. */
function Passkeys() {
  const store = useStore()
  const mounted = useMounted()
  const [list, setList] = useState<PasskeyList | null>(null)
  const [unread, setUnread] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [name, setName] = useState('')
  const read = useCallback(() => {
    setUnread(false)
    store.passkeyList().then(
      (read) => { if (mounted.current) setList(read) },
      () => { if (mounted.current) setUnread(true) },
    )
  }, [store, mounted])
  useEffect(read, [read])
  if (unread) {
    return <div className="field">
      <span>{w.passkeys}</span>
      <span className="account-do muted">{w.unread} <button type="button" className="button button-small" onClick={read}>{w.retry}</button></span>
    </div>
  }
  if (!list) return null
  const add = async () => {
    setBusy(true); setProblem(null)
    const outcome = await store.addPasskey()
    if (!mounted.current) return
    setBusy(false)
    if (outcome.ok) setList(outcome.made)
    else setProblem(addProblemText(outcome, w.passkeyFailed))
  }
  const rename = async (passkey: PasskeyEntry) => {
    const typed = name.trim()
    if ((passkey.name ?? '') === typed) { setRenaming(null); return }
    setBusy(true); setProblem(null)
    const outcome = await store.namePasskey(passkey.id, typed || null)
    if (!mounted.current) return
    setBusy(false)
    if (outcome.ok) { setList(outcome.made); setRenaming(null) }
    else setProblem(confirmationProblemText(outcome, w.renameFailed))
  }
  const remove = async (passkey: PasskeyEntry) => {
    setBusy(true); setProblem(null)
    const outcome = await store.removePasskey(passkey.id)
    if (!mounted.current) return
    setBusy(false)
    if (outcome.ok) setList(outcome.made)
    else setProblem(outcome.problem === 'last_passkey' ? w.lastPasskey : addProblemText(outcome, w.removeFailed))
  }
  return <>
    <div className="field">
      <span>{w.passkeys}</span>
      {list.count > 0
        ? <span className="settings-value account-passkeys">
            <span>{w.count(list.count)}</span>
            <ul>{list.passkeys.map((passkey) => <li key={passkey.id}>
              {renaming === passkey.id
                ? <form className="account-rename" onSubmit={(event) => { event.preventDefault(); void rename(passkey) }}>
                    <input type="text" name="passkey-name" aria-label={w.passkeyName} maxLength={64} autoComplete="off" value={name} readOnly={busy} autoFocus
                      onChange={(event) => setName(event.target.value)} />
                    <button type="button" className="button button-small" disabled={busy} onClick={() => { setRenaming(null); setProblem(null) }}>{w.cancel}</button>
                    <button type="submit" className="button button-small button-primary" disabled={busy}>{w.save}</button>
                  </form>
                : <span className="account-passkey">
                    <span>{passkey.name ?? w.unnamed}</span>
                    <span className="account-do">
                      <button type="button" className="button button-small" disabled={busy} onClick={() => { setRenaming(passkey.id); setName(passkey.name ?? ''); setProblem(null) }}>{w.rename}</button>
                      <button type="button" className="button button-small" disabled={busy} onClick={() => void remove(passkey)}>{w.remove}</button>
                    </span>
                  </span>}
              <span className="muted">{w.added} <Time iso={passkey.created_at} /></span>
              <span className="muted">{passkey.last_used_at ? <>{w.lastUsed} <Time iso={passkey.last_used_at} /></> : w.neverUsed}</span>
            </li>)}</ul>
          </span>
        : <span className="settings-value account-passkeys">
            <span>{w.none}</span>
            <span className="muted">{w.why}</span>
          </span>}
    </div>
    {problem && <p className="account-problem" role="alert">{problem}</p>}
    <div className="account-actions">
      {list.count < PASSKEYS_MAX
        ? <button type="button" className="button" disabled={busy} onClick={() => void add()}>{w.addPasskey}</button>
        : <span className="muted">{w.most}</span>}
    </div>
  </>
}
